import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, BookOpen } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { blogService, resolveImageUrl } from '../services/blogService';
import type { BlogArticleListItem } from '../types/blog';

const formatDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

const Blog: React.FC = () => {
  const [articles, setArticles] = useState<BlogArticleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    blogService
      .listPublished()
      .then(setArticles)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <article className="pt-24 pb-16 bg-neutral-offwhite min-h-screen">
      <SEOHead
        title="Mag'Moman — Le blog bien-être périnatal"
        description="Mag'Moman, le magazine de Doux ce Moman : conseils, astuces et actualités autour de la grossesse, du massage prénatal, du spa bébé et de la parentalité, près de Besançon."
        path="/blog"
      />
      <div className="container mx-auto px-4">
        {/* Hero */}
        <header className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-rose/15 text-brand font-nunito text-sm font-semibold mb-4">
            <BookOpen className="w-4 h-4" />
            Le magazine bien-être
          </div>
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-neutral-ink mb-6">
            Mag'Moman
          </h1>
          <p className="font-nunito text-neutral-body max-w-2xl mx-auto text-lg">
            Conseils, astuces et inspirations autour de la grossesse, du bien-être de bébé et de la
            parentalité. Prenez un moment pour vous, on s'occupe du reste.
          </p>
        </header>

        {/* States */}
        {loading && <p className="text-center text-neutral-body font-nunito py-12">Chargement des articles…</p>}

        {error && !loading && (
          <p className="text-center text-red-600 font-nunito py-12">
            Impossible de charger les articles pour le moment.
          </p>
        )}

        {!loading && !error && articles.length === 0 && (
          <div className="text-center py-16">
            <BookOpen className="w-14 h-14 text-accent-rose/40 mx-auto mb-4" />
            <p className="font-nunito text-neutral-body text-lg">
              Les premiers articles arrivent très bientôt. Revenez nous voir !
            </p>
          </div>
        )}

        {/* Grid */}
        {!loading && !error && articles.length > 0 && (
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article) => (
              <Link
                key={article.id}
                to={`/blog/${article.slug}`}
                className="group flex flex-col bg-neutral-offwhite rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="aspect-[16/10] overflow-hidden bg-accent-sand/30">
                  {article.cover_image_url ? (
                    <img
                      src={resolveImageUrl(article.cover_image_url)}
                      alt={article.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-accent-rose/40" />
                    </div>
                  )}
                </div>

                <div className="flex flex-col flex-grow p-6">
                  {article.published_at && (
                    <div className="flex items-center gap-2 text-xs text-neutral-body/70 font-nunito mb-3">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(article.published_at)}
                      {article.author && <span>· {article.author}</span>}
                    </div>
                  )}
                  <h2 className="font-playfair text-xl font-bold text-neutral-ink mb-3 group-hover:text-brand transition-colors">
                    {article.title}
                  </h2>
                  {article.excerpt && (
                    <p className="font-nunito text-neutral-body text-sm leading-relaxed line-clamp-3 mb-4">
                      {article.excerpt}
                    </p>
                  )}
                  <span className="mt-auto inline-flex items-center gap-1.5 text-brand font-nunito font-semibold text-sm">
                    Lire l'article
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </section>
        )}
      </div>
    </article>
  );
};

export default Blog;
